/**
 * 2026-06-07 — Inline "?" help affordance.
 *
 * Renders a small clickable "?" next to a UI element. Click toggles a
 * popover with the help title + body. Visibility of all "?" icons is
 * gated by the help-mode toggle in the scenario-runner header; when
 * the toggle is OFF, this component renders nothing.
 *
 * Help item bodies use blank-line-separated paragraphs.
 */

import { useEffect, useRef, useState } from 'react';
import './HelpIcon.css';
import { getHelpItem } from './helpRegistry';
import { usePlayerSettings } from '../../services/playerSettings';

export interface HelpIconProps {
  /** Help registry id — must be present in helpRegistry.ts. */
  id: string;
  /** Optional label override (defaults to the help item title). */
  label?: string;
  testidPrefix?: string;
  /** Position of the popover relative to the icon. */
  side?: 'right' | 'left' | 'below';
}

export function HelpIcon({
  id,
  label,
  testidPrefix = `br-help-${id}`,
  side = 'right',
}: HelpIconProps) {
  const { helpVisibleEnabled } = usePlayerSettings();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const item = getHelpItem(id);

  useEffect(() => {
    if (!open) return;
    function handleOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (
        buttonRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('mousedown', handleOutside);
    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('mousedown', handleOutside);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  if (!helpVisibleEnabled) return null;
  if (item === null) return null;

  const labelText = label ?? item.title;

  return (
    <span className="br-help-icon-wrapper" data-testid={testidPrefix}>
      <button
        ref={buttonRef}
        type="button"
        className="br-help-icon"
        data-testid={`${testidPrefix}-button`}
        aria-label={`Help: ${labelText}`}
        aria-expanded={open}
        onClick={() => setOpen((s) => !s)}
      >
        ?
      </button>
      {open && (
        <div
          ref={popoverRef}
          className={`br-help-popover br-help-popover-${side}`}
          data-testid={`${testidPrefix}-popover`}
          role="dialog"
          aria-labelledby={`${testidPrefix}-title`}
        >
          <header className="br-help-popover-header">
            <h4 id={`${testidPrefix}-title`} className="br-help-popover-title">
              {item.title}
            </h4>
            <button
              type="button"
              className="br-help-popover-close"
              data-testid={`${testidPrefix}-close`}
              aria-label="Close help"
              onClick={() => setOpen(false)}
            >
              ✕
            </button>
          </header>
          <div className="br-help-popover-body">
            {item.body.split('\n\n').map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>
      )}
    </span>
  );
}

export default HelpIcon;
